/**
 * useNavStyle — chrome preference (Sidebar vs Aperture Bar).
 *
 * The web app ships with two nav chromes:
 *   • sidebar (default) — the left rail
 *   • aperture — the iOS-parity bottom Aperture Bar
 *
 * FOUNDERS-GO-3 (Oct 2) — the RAIL is the default again. Rennan: "kill
 * the aperture bar in the setup, and by default have it set to the
 * rail." Setup no longer asks (its chooser step is retired), so every
 * studio that hasn't picked starts on the rail; the Aperture Bar is one
 * switch away in Settings → Appearance.
 *
 * (CHROME-1 had made the Aperture Bar the default, when setup still
 * asked everyone to choose.)
 *
 * Same contract as every flip of this default: NO STORED PREFERENCE →
 * the default; an EXPLICIT stored value always wins. A studio that
 * picked the Aperture Bar — in setup, the welcome sequence or Settings —
 * has the literal 'aperture' stored and keeps it. Only a missing or
 * unrecognised key falls through to 'sidebar'.
 *
 * The choice lives in localStorage and is echoed cross-tab via
 * the `storage` event so a change in Settings updates every
 * open Orbit tab within a tick.  Same pattern as useNewShell().
 */
import { useCallback, useEffect, useState } from 'react';

export type NavStyle = 'sidebar' | 'aperture';

const STORAGE_KEY = 'orbit.nav_style';
const DEFAULT: NavStyle = 'sidebar';

/** Coerce a raw localStorage value to a NavStyle.  `null` (never
 *  chosen) and anything unrecognised fall through to the default;
 *  an explicit 'aperture' is honoured. */
function coerce(v: string | null): NavStyle {
  if (v === 'sidebar') return 'sidebar';
  if (v === 'aperture') return 'aperture';
  return DEFAULT;
}

function read(): NavStyle {
  if (typeof window === 'undefined') return DEFAULT;
  try {
    return coerce(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT;
  }
}

export function useNavStyle(): [NavStyle, (next: NavStyle) => void] {
  const [value, setValue] = useState<NavStyle>(read);

  useEffect(() => {
    // Cross-tab + cross-subscriber sync — the `storage` event
    // fires on OTHER tabs natively.  We dispatch it manually
    // from set() so subscribers in the SAME tab pick up changes
    // without a refresh (matches useNewShell).
    const handler = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setValue(coerce(e.newValue));
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const set = useCallback((next: NavStyle) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
      window.dispatchEvent(
        new StorageEvent('storage', {
          key: STORAGE_KEY,
          newValue: next,
        }),
      );
    } catch {
      /* private mode — in-memory only for this session */
    }
    setValue(next);
  }, []);

  return [value, set];
}
